import { type ChangeEvent, useEffect, useRef, useState, type FC } from 'react'
import { styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PhotoUpload } from '@dhl/receiver-design'
import { Alert, Button, Stack, Box } from '@dhl-ui/components'
import { CameraIcon, DeleteIcon, EditSolidIcon } from '@dhl-ui/icons'

type Props = {
  value: Blob | null
  error?: string
  touched?: boolean
  photoUploadValue: string
  onImageDelete: () => void
  onPhotoUploadChange: (event: ChangeEvent<HTMLInputElement>) => void
}

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  width: '100%',
  '> img': {
    marginBottom: theme.spacing(2),
    width: 350,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}))

export const AgreedPlacePhotoUpload: FC<Props> = ({
  value,
  error,
  touched,
  photoUploadValue,
  onImageDelete,
  onPhotoUploadChange,
}) => {
  const imagePreviewContainerRef = useRef<HTMLDivElement | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined)
  const { t } = useTranslation()

  useEffect(() => {
    if (!value) {
      setPreviewUrl(undefined)
      return undefined
    }

    const objectUrl = URL.createObjectURL(value)
    setPreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [value])

  return (
    <Stack gap={2}>
      {value && (
        <ImagePreviewContainer data-test="agreed-place-image-preview" ref={imagePreviewContainerRef}>
          <img src={previewUrl} alt={t('interventions.agreedPlace.image.imageAltText')} style={{ margin: 0 }} />
        </ImagePreviewContainer>
      )}

      {touched && error && (
        <Alert data-test="agreed-place-image-error" severity="error">
          {error}
        </Alert>
      )}

      <PhotoUpload
        buttonIcon={previewUrl ? <EditSolidIcon /> : <CameraIcon />}
        buttonText={
          previewUrl
            ? t('interventions.agreedPlace.image.editImageButton')
            : t('interventions.agreedPlace.image.selectImageButton')
        }
        data-testid="agreed-place-image-select"
        value={photoUploadValue}
        onChange={onPhotoUploadChange}
      />

      {!!previewUrl && (
        <Button
          fullWidth
          color="alternative"
          variant="outlined"
          data-test="agreed-place-image-delete"
          startIcon={<DeleteIcon />}
          onClick={onImageDelete}
        >
          {t('interventions.agreedPlace.image.deleteImageButton')}
        </Button>
      )}
    </Stack>
  )
}
